import { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Group,
  Text,
  Loader,
  Accordion,
  Select,
  Stack,
  Slider,
  TextInput,
  Textarea,
  Alert,
} from "@mantine/core";
import { getGenerationOptions, generateImage, getCustomModels } from "../api";
import { IconCoins } from "@tabler/icons-react";
import mixpanel from "mixpanel-browser";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { Link } from "wouter";
import { useForm } from "@mantine/form";

function isIterable(obj) {
  // checks for null and undefined
  if (obj == null) {
    return false;
  }
  return typeof obj[Symbol.iterator] === "function";
}

export default function GeneratorOptions() {
  const [, setConfig] = useState({});
  const jobs = useStore((state) => state.jobs);
  const setJobs = useStore((state) => state.setJobs);
  const profile = useStore((state) => state.profile);
  const [optionSource, setOptionSource] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedCustomModel, setSelectedCustomModel] = useState(null);
  const [customModels, setCustomModels] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [filters, setFilters] = useState({ environment: "", action: "" });

  const generatorConfig = useStore((state) => state.generatorConfig);
  const plugins = useStore((state) => state.pluginData);

  const form = useForm({
    // TBD: generatorCOnfig might contain arrays, which forces the chips to become multi-select... That is BAD...
    initialValues: generatorConfig || {
      aspectRatio: "portrait",
      creativity: 3.5,
      numImages: 1,
      seed: "",
      config: {},
      styleTemplateId: "",
    },
    validate: {
      seed: (value) =>
        value === "" || /^[0-9]+$/.test(value) ? null : "Seed must be a number",
    },
  });

  const calculateThreshold = (config) => {
    let thresholdCount = 0;
    if (selectedCustomModel) thresholdCount += 1;

    for (var key in config) {
      let selVal = config[key];
      let relevantOption = options?.options?.find((o) => o.title === key);
      if (!relevantOption || !relevantOption.values) continue;
      if (!isIterable(selVal)) continue;

      for (var configuredValue of selVal) {
        let selectedValue = relevantOption.values.find(
          (v) => v.titleUser === configuredValue
        );
        if (selectedValue) thresholdCount += selectedValue.threshold;
      }
    }

    return thresholdCount;
  };

  const threshold = calculateThreshold(form.values.config);
  const stylesConflicts = threshold > 4;

  useEffect(() => {
    if (generatorConfig) {
      console.debug("Generator config changed, setting", generatorConfig);
      form.setValues(generatorConfig);
      form.setInitialValues(generatorConfig);
    }
  }, [generatorConfig]);

  useEffect(() => {
    getCustomModels(optionSource).then((data) => {
      let selectData = data.map((p) => ({
        label: p.name ? `${p.name} (${p.id})` : p.id,
        value: p.id,
      }));
      setCustomModels(selectData);
    });
  }, [optionSource]);

  useEffect(() => {
    getGenerationOptions(optionSource).then((data) => {
      setOptions(data);
      let conf = {};
      for (let element of data.options) {
        conf[element.title] = [];
      }
      setConfig(conf);
      // defaultDto.config = conf;
      // setOptionsLoaded(true);
      try {
        mixpanel.track("Generation options loaded");
      } catch {}
    });
  }, [optionSource]);

  const pluginOptions =
    plugins?.map((p) => ({ label: p.pluginTitle, value: p.id })) || [];

  const styles = options?.presets?.flatMap((p) => p.values);

  const handleChipClick = (option) => (event) => {
    const keyName = option.title;
    form.setFieldValue("config." + keyName, null);
  };

  return (
    <Stack p="xs" gap="xs">
      <Select
        description={
          profile?.plan === "Free" ? (
            <>
              You need to{" "}
              <Link to="/payment/plans">upgrade to create custom models</Link>{" "}
              of people or characters
            </>
          ) : (
            <>
              Select one of your <Link to="/training-datasets">AI models</Link>
            </>
          )
        }
        label="Model"
        clearable
        disabled={profile?.plan === "Free" || customModels.length === 0}
        onChange={setSelectedCustomModel}
        value={selectedCustomModel}
        data={customModels}
      />

      <form
        onSubmit={form.onSubmit((values) => {
          setIsSubmitting(true);
          var dtoConfig = {};
          for (var x in values.config) {
            const val = values.config[x];
            // check if val is an array:
            if (Array.isArray(val)) dtoConfig[x] = val;
            else dtoConfig[x] = [val];
            // if (isIterable(val)) dtoConfig[x] = [...val];
            // shouldn't happen, but it does...
            // else
            // dtoConfig[x] = val;
          }
          const dto = {
            sourceId: optionSource,
            customModelId: selectedCustomModel,
            seed: values.seed === "" ? null : values.seed,
            prompt: values.prompt,
            numImages: values.numImages,
            creativity: values.creativity,
            config: dtoConfig,
            aspectRatio: values.aspectRatio,
            styletemplateId: values.styleTemplateId,
          };
          generateImage(dto)
            .then((data) => {
              let newJob = {
                config: dtoConfig,
                uri: "c1b.jpg",
                clientId: Math.random().toString(36).substr(2, 9),
                ...data,
              };
              setJobs([newJob, ...jobs]);
              // let newCard = { jobId: data.jobId, config: dtoConfig };
              // setCards([newCard, ...cards]);
              try {
                mixpanel.track("Generation Start");
              } catch {}
              setIsSubmitting(false);
            })
            .catch((error) => {
              notifications.show({
                title: "An error occurred!",
                message: error.message,
                color: "red",
              });
              setIsSubmitting(false);
            });
        })}
      >
        <Stack gap="sm">
          <Textarea
            label="Prompt"
            key={form.key("prompt")}
            {...form.getInputProps(`prompt`)}
            minRows={3}
            maxRows={6}
            autosize
          />

          {styles?.length > 0 && (
            <Select
              key={form.key("styleTemplateId")}
              label="Style"
              clearable
              searchable
              data={styles.map((option) => ({
                label: option.titleUser,
                value: option.id,
              }))}
              {...form.getInputProps(`styleTemplateId`)}
            />
          )}

          <div>
            <Text fz="sm">Aspect Ratio</Text>
            <Chip.Group
              size="xs"
              {...form.getInputProps(`aspectRatio`)}
              key={form.key("aspectRatio")}
            >
              <Group gap="xs">
                <Chip variant="light" size="xs" value="portrait">
                  Portrait
                </Chip>
                <Chip variant="light" size="xs" value="landscape">
                  Landscape
                </Chip>
                <Chip variant="light" size="xs" value="square">
                  Square
                </Chip>
              </Group>
            </Chip.Group>
          </div>

          <Slider
            label="Creativity"
            {...form.getInputProps(`creativity`)}
            key={form.key("creativity")}
            color="blue"
            mt="sm"
            mb="md"
            defaultValue={3.5}
            min={3.0}
            max={7.0}
            step={0.1}
            marks={[
              { value: 3.5, label: "Adhering" },
              { value: 6.5, label: "Creative" },
            ]}
          />

          <TextInput
            {...form.getInputProps(`seed`)}
            key={form.key("seed")}
            label="Seed"
            description="Same seed generates similar images"
          />

          {/* <NumberInput
            {...form.getInputProps(`numImages`)}
            key={form.key("numImages")}
            disabled
            min={1}
            max={1}
            label="Number of Images"
            description="Generate images in parallel (soon)"
          /> */}
          {pluginOptions?.length > 0 && (
            <Select
              description="Load plugin options & styles"
              label="Style Plugins"
              onChange={setOptionSource}
              value={optionSource}
              clearable
              data={[...pluginOptions]}
            />
          )}
        </Stack>
        <Accordion>
          {!options && <Loader />}

          {options &&
            options.options &&
            options.options
              .sort(function (b, a) {
                var x = a.title.toLowerCase();
                var y = b.title.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
              })
              .map((option) => (
                <Accordion.Item key={option.title} value={option.title}>
                  <Accordion.Control>{option.title}</Accordion.Control>
                  <Accordion.Panel>
                    {option.values.length > 10 && (
                      <TextInput
                        size="xs"
                        mb="xs"
                        value={filters[option.title]}
                        onChange={(evt) =>
                          setFilters({
                            ...filters,
                            [option.title]: evt.target.value,
                          })
                        }
                      />
                    )}
                    <Chip.Group
                      multiple={option.allowMulti}
                      {...form.getInputProps(`config.${option.title}`)}
                    >
                      <Group gap="xs">
                        {option.values
                          .filter((value) => {
                            if (!filters[option.title]) return true;
                            return value.titleUser
                              .toLowerCase()
                              .includes(filters[option.title].toLowerCase());
                          })
                          .sort(function (a, b) {
                            var x = a.titleUser.toLowerCase();
                            var y = b.titleUser.toLowerCase();
                            return x < y ? -1 : x > y ? 1 : 0;
                          })
                          .map((value) => (
                            // <Tooltip
                            //   label={value.tooltip}
                            //   key={`${option.title}-${value.titleUser}`}
                            // >
                            <Chip
                              key={`${option.title}-${value.titleUser}`}
                              variant="light"
                              onClick={handleChipClick(option)}
                              size="xs"
                              value={value.titleUser}
                            >
                              {value.titleUser}
                            </Chip>
                            // </Tooltip>
                          ))}
                      </Group>
                    </Chip.Group>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
        </Accordion>

        {stylesConflicts && (
          <Alert color="red">Too many conflicting options configured</Alert>
        )}

        <Button
          variant="light"
          rightSection={
            <Group gap={4}>
              <IconCoins stroke={1.5} size="15" /> {threshold > 2 ? "60" : "30"}
            </Group>
          }
          mt="md"
          type="submit"
          disabled={stylesConflicts}
          loading={isSubmitting}
        >
          Create
        </Button>
      </form>
    </Stack>
  );
}
